import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

const styles = {
  page: {
    minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#0F172A', fontFamily: 'system-ui, sans-serif',
  },
  card: {
    background: '#1E293B', borderRadius: 14, padding: '2rem', width: '100%', maxWidth: 380,
    boxShadow: '0 4px 32px rgba(0,0,0,0.25)', border: '1px solid #334155',
    backdropFilter: 'blur(8px)',
  },
  title: { margin: '0 0 1.5rem', fontSize: '1.4rem', fontWeight: 700, color: '#F8FAFC' },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#94A3B8', marginBottom: 4 },
  input: {
    width: '100%', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1.5px solid #334155',
    fontSize: '1rem', boxSizing: 'border-box', outline: 'none', marginBottom: '1rem',
    background: '#1E293B', color: '#F8FAFC',
  },
  btn: {
    width: '100%', padding: '0.75rem', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff', fontSize: '1rem', fontWeight: 700,
    cursor: 'pointer', marginTop: 4,
  },
  error: { color: '#F87171', fontSize: '0.875rem', marginBottom: '1rem' },
};

export default function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const showEmailError = emailTouched && form.email && !isValidEmail(form.email);

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const email = form.email.trim();
    if (!isValidEmail(email)) { setError(t('login.emailError')); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Login failed'); return; }
      login(data.accessToken, data.refreshToken, remember);
      // Route based on role from JWT
      try {
        const claims = JSON.parse(atob(data.accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        navigate(claims.role === 'ADMIN' ? '/admin/monitor/system' : '/admin/queue');
      } catch {
        navigate('/admin/queue');
      }
    } catch {
      setError(t('login.networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <Link to="/" style={{ textDecoration: 'none', display: 'block', textAlign: 'center', marginBottom: '1rem' }}>
          <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#7C3AED' }}>vQueue</span>
        </Link>
        <h1 style={styles.title}>{t('login.title')}</h1>
        {error && <p style={styles.error}>{error}</p>}
        <form onSubmit={handleSubmit}>
          <label style={styles.label}>{t('login.emailLabel')}</label>
          <input style={{ ...styles.input, ...(showEmailError ? { borderColor: '#EF4444' } : {}) }} type="email" placeholder={t('login.emailPlaceholder')} value={form.email} onChange={set('email')} onBlur={() => setEmailTouched(true)} required />
          {showEmailError && <p style={{ color: '#EF4444', fontSize: '0.8rem', margin: '-0.75rem 0 0.75rem' }}>{t('login.emailError')}</p>}
          <label style={styles.label}>{t('login.passwordLabel')}</label>
          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <input style={{ ...styles.input, marginBottom: 0, paddingRight: '3rem' }} type={showPw ? 'text' : 'password'} placeholder={t('login.passwordPlaceholder')} value={form.password} onChange={set('password')} required />
            <button type="button" onClick={() => setShowPw((v) => !v)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#94A3B8', padding: 4 }}>
              {showPw ? t('login.hidePassword') : t('login.showPassword')}
            </button>
          </div>
          <p style={{ textAlign: 'right', margin: '0 0 0.5rem', fontSize: '0.8rem' }}>
            <Link to="/admin/forgot-password" style={{ color: '#7C3AED', textDecoration: 'none', fontWeight: 500 }}>
              {t('login.forgotPassword')}
            </Link>
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#94A3B8', margin: '0.25rem 0 1rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            {t('login.rememberMe')}
          </label>
          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? t('login.submitting') : t('login.submit')}
          </button>
        </form>
        <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.85rem', color: '#94A3B8' }}>
          {t('login.noAccount')} <Link to="/admin/signup" style={{ color: '#7C3AED', textDecoration: 'none', fontWeight: 600 }}>{t('login.signUp')}</Link>
        </p>
      </div>
    </div>
  );
}
